/* External dependencies */
import { Epic, combineEpics, ofType } from 'redux-observable';
import { AnyAction } from 'redux';
import { switchMap, catchError, map } from 'rxjs/operators';
import { from, of } from 'rxjs';
import CognitoClient from '@mancho.devs/cognito';

/* Local dependencies */
import {
  LoginActionTypes,
  signInRequest,
  signInSucceeded,
  signInFailed,
  signUpRequest,
  signUpSucceeded,
  signUpFailed,
  confirmSignUpSuccess,
  confirmSignUpFailed,
  forgotPasswordSucceeded,
  forgotPasswordFailed,
  confirmPasswordSucceeded,
  confirmPasswordFailed,
  signOutSucceeded,
  signOutFailed,
} from './actions';

const cognito = new CognitoClient({
  UserPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID!,
  ClientId: process.env.GATSBY_COGNITO_CLIENT_ID!,
});

export const signInEpic: Epic = (action$) =>
  action$.pipe(
    ofType(LoginActionTypes.SIGNIN),
    switchMap((action: ReturnType<typeof signInRequest>) =>
      from(cognito.signIn(action.username, action.password)).pipe(
        map((user: any) => {
          const email = user?.getUsername?.() || action.username;
          return signInSucceeded({ email });
        }),
        catchError((err) => of(signInFailed(err))),
      ),
    ),
  );

export const signUpEpic: Epic = (action$, _state$, { cognito }) =>
  action$.pipe(
    ofType(LoginActionTypes.SIGNUP),
    switchMap((action: ReturnType<typeof signUpRequest>) =>
      from(cognito.signUp(action.username, action.password)).pipe(
        map(() => signUpSucceeded()),
        catchError((err) => of(signUpFailed(err))),
      ),
    ),
  );

export const confirmSignUpEpic: Epic = (action$) =>
  action$.pipe(
    ofType(LoginActionTypes.CONFIRM_SIGNUP),
    switchMap((action: AnyAction) =>
      from(cognito.signUpConfirmCode(action.username, action.code)).pipe(
        map(() => confirmSignUpSuccess()),
        catchError((err) => of(confirmSignUpFailed(err))),
      ),
    ),
  );

export const forgotPasswordEpic: Epic = (action$) =>
  action$.pipe(
    ofType(LoginActionTypes.FORGOT_PASSWORD),
    switchMap((action: AnyAction) =>
      from(cognito.forgotPassword(action.username)).pipe(
        map(() => forgotPasswordSucceeded(action.username)),
        catchError((err) => of(forgotPasswordFailed(err))),
      ),
    ),
  );

export const confirmPasswordEpic: Epic = (action$) =>
  action$.pipe(
    ofType(LoginActionTypes.CONFIRM_PASSWORD),
    switchMap((action: AnyAction) =>
      from(
        cognito.confirmPassword(
          action.username,
          action.verificationCode,
          action.newPassword,
        ),
      ).pipe(
        switchMap(() => [
          confirmPasswordSucceeded(),
          signInRequest(action.username, action.newPassword),
        ]),
        catchError((err) => of(confirmPasswordFailed(err))),
      ),
    ),
  );

export const signOutEpic: Epic = (action$) =>
  action$.pipe(
    ofType(LoginActionTypes.SIGNOUT),
    switchMap(() =>
      from(cognito.signOut()).pipe(
        map(() => signOutSucceeded()),
        catchError((err) => of(signOutFailed(err))),
      ),
    ),
  );

export const rootEpic = combineEpics(
  signInEpic,
  signUpEpic,
  confirmSignUpEpic,
  forgotPasswordEpic,
  confirmPasswordEpic,
  signOutEpic,
);
